export const SUPPORTED_LOCALES = ["en", "ja"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: Locale = "en";
const LOCALE_STORAGE_KEY = "dewey:lang";
export const LOCALE_URL_PARAM = "lang";

const isLocale = (v: unknown): v is Locale =>
  typeof v === "string" && (SUPPORTED_LOCALES as readonly string[]).includes(v);

const fromUrl = (search: string): Locale | null => {
  const v = new URLSearchParams(search).get(LOCALE_URL_PARAM);
  return isLocale(v) ? v : null;
};

const fromLocalStorage = (storage: Pick<Storage, "getItem">): Locale | null => {
  try {
    const v = storage.getItem(LOCALE_STORAGE_KEY);
    return isLocale(v) ? v : null;
  } catch {
    return null;
  }
};

// Matches "ja", "ja-JP", "en", "en-US", etc. Strips region tag.
const fromBrowser = (languages: readonly string[]): Locale | null => {
  for (const tag of languages) {
    const base = tag.split("-")[0]?.toLowerCase();
    if (isLocale(base)) return base;
  }
  return null;
};

type ResolveLocaleEnv = {
  search: string;
  storage: Pick<Storage, "getItem">;
  languages: readonly string[];
};

export const resolveLocale = (env: ResolveLocaleEnv): Locale =>
  fromUrl(env.search) ?? fromLocalStorage(env.storage) ?? fromBrowser(env.languages) ?? DEFAULT_LOCALE;

const SAFE_NULL_STORAGE: Pick<Storage, "getItem"> = { getItem: () => null };

const safeWindowStorage = (): Pick<Storage, "getItem"> => {
  if (typeof window === "undefined") return SAFE_NULL_STORAGE;
  try {
    return window.localStorage ?? SAFE_NULL_STORAGE;
  } catch {
    return SAFE_NULL_STORAGE;
  }
};

export const resolveLocaleFromWindow = (): Locale =>
  resolveLocale({
    search: typeof window === "undefined" ? "" : window.location.search,
    storage: safeWindowStorage(),
    languages: typeof navigator === "undefined" ? [] : (navigator.languages ?? [navigator.language]),
  });

export const persistLocale = (locale: Locale): void => {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // ignore (private mode / quota)
  }
};
